import apiClient from './ApiClient';

export class ServiceRepository {
	constructor(client) {
		this.client = client;
		this.basePath = '/api/service-items';
	}

	/**
	 * Get all service items with optional filtering
	 * @param {Object} params - Optional query parameters
	 * @param {string} params.type - Filter by type ('service' or 'product')
	 * @param {string} params.categoryId - Filter by category UUID
	 * @param {boolean} params.activeOnly - Show only active items (default: true)
	 * @returns {Promise<Array>} Array of service items
	 */
	async list(params = {}) {
		return this.client.get(this.basePath, params);
	}

	/**
	 * Get service item by ID
	 * @param {string} id - Service item UUID
	 * @returns {Promise<Object>} Service item object
	 */
	async getById(id) {
		return this.client.get(`${this.basePath}/${id}`);
	}

	/**
	 * Create a new service item - Admin only
	 * @param {Object} payload
	 * @param {string} payload.name - Service/product name
	 * @param {string} payload.description - Description
	 * @param {number} payload.price - Price
	 * @param {number} payload.durationInMinutes - Duration (for services)
	 * @param {string} payload.categoryId - Category UUID
	 * @param {string} payload.type - 'service' or 'product'
	 * @param {string} [payload.imageUrl] - Image URL (optional)
	 * @param {boolean} [payload.isPopular] - Mark as popular (optional)
	 * @param {number} [payload.initialStock] - Initial stock quantity (for products only)
	 * @param {number} [payload.lowStockThreshold] - Low stock alert threshold (for products only, defaults to 5)
	 * @returns {Promise<{id: string}>}
	 */
	async create(payload) {
		return this.client.post(this.basePath, payload);
	}

	/**
	 * Update an existing service item - Admin only
	 * @param {string} id - Service item UUID
	 * @param {Object} payload - Updated fields (same as create, except type cannot be changed)
	 * @param {number} [payload.lowStockThreshold] - Low stock alert threshold (for products only)
	 * @returns {Promise<{success: boolean}>}
	 */
	async update(id, payload) {
		return this.client.put(`${this.basePath}/${id}`, payload);
	}

	/**
	 * Delete (soft delete/deactivate) a service item - Admin only
	 * @param {string} id - Service item UUID
	 * @returns {Promise<{success: boolean, message: string}>}
	 */
	async delete(id) {
		return this.client.delete(`${this.basePath}/${id}`);
	}

	// Alias method to match current usage in UI
	async remove(id) {
		return this.delete(id);
	}
}

export default new ServiceRepository(apiClient);
